import React from 'react';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../../utils/api';
import './FeeReceipt.css';

const API_BASE = BASE_URL;
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getYearLabel = (student) => student?.currentYear || null;

// ── Single slip ────────────────────────────────────────────────────────────
const Slip = ({ allocation, payment, college, student, collegeLogo, copyLabel }) => {
  const receiptNo   = payment.receiptNo || `RCP-${Date.now()}`;
  const receiptDate = fmtDate(payment.paidAt);
  const paidAmount  = Number(payment.paidAmount || 0);
  
  const balance = Number(payment.balanceAfterTransaction ?? 0);
  const feeName = payment.feeName || 'Fee';
  const studentClass = payment.studentClass || student?.class?.name || 'N/A';
  const feeSession = payment.sessionName || allocation?.feeSession || 'N/A';
  
  const yearLabel = getYearLabel(student);

  const items = payment.items?.length > 0
    ? payment.items.filter(i => Number(i.amountPaid) > 0)
    : [{ type: feeName, amountPaid: paidAmount }];

  const modes = payment.paymentModes?.filter(m => Number(m.amount) > 0).length > 0
    ? payment.paymentModes.filter(m => Number(m.amount) > 0)
    : [{ mode: 'Cash', referenceNo: '—', amount: paidAmount, date: payment.paidAt }];

  return (
    <div className="fee-receipt-slip">

      {/* ── Header: logo left, college info center ── */}
      <div className="fee-receipt-header">
        {collegeLogo && (
          <div className="fee-receipt-logo-box">
            <img 
              src={collegeLogo} 
              alt="Logo" 
              className="fee-receipt-logo-img"
              onError={e => { e.target.onerror = null; e.target.parentNode.style.display = 'none'; }} 
            />
          </div>
        )}
        <div className={`fee-receipt-header-info${collegeLogo ? '' : ' fee-receipt-header-info-full'}`}>
          <h1 className="fee-receipt-college-name">{college.name || 'College Name'}</h1>
          {college.titleHeader && <p className="fee-receipt-college-title">{college.titleHeader}</p>}
          {college.address && <p className="fee-receipt-college-sub">{college.address}</p>}
        </div>
      </div>

      {/* ── Title bar with copy name (saves vertical space) ── */}
      <div className="fee-receipt-title-bar">
        Fee Receipt ({copyLabel})
      </div>

      {/* ── S.No left | Date right ── */}
      <div className="fee-receipt-meta-row">
        <span><strong>S. No.</strong> {receiptNo}</span>
        <span><strong>Date:</strong> {receiptDate}</span>
      </div>

      {/* ── Student details ── */}
      <div className="fee-receipt-details-box">
        <div className="fee-receipt-detail-row">
          <span className="fee-receipt-detail-label">Name</span>
          <span className="fee-receipt-detail-value fee-receipt-bold">{student.name || '—'}</span>
        </div>
        <div className="fee-receipt-detail-row">
          <span className="fee-receipt-detail-label">Father's Name</span>
          <span className="fee-receipt-detail-value">{student.fatherName || '—'}</span>
        </div>
        <div className="fee-receipt-detail-row">
          <span className="fee-receipt-detail-label">Year</span>
          <span className="fee-receipt-detail-value">{yearLabel || '—'}</span>
        </div>
        <div className="fee-receipt-detail-row">
          <span className="fee-receipt-detail-label">Class</span>
          <span className="fee-receipt-detail-value">{studentClass}</span>
        </div>
        <div className="fee-receipt-detail-row">
          <span className="fee-receipt-detail-label">Fee Session</span>
          <span className="fee-receipt-detail-value">{feeSession}</span>
        </div>
      </div>

      {/* ── Particulars + Payment Modes side by side ── */}
      <div className="fee-receipt-grid-2">

        {/* Left — Particulars */}
        <div className="fee-receipt-box">
          <div className="fee-receipt-box-head">Particulars</div>
          <div className="fee-receipt-box-body">
            {items.map((item, i) => (
              <div className="fee-receipt-item-row" key={i}>
                <span>{item.type || 'Fee'}</span>
                <span className="fee-receipt-bold">₹ {fmt(item.amountPaid)}</span>
              </div>
            ))}
          </div>
          <div className="fee-receipt-box-total">
            <span>Total Paid</span>
            <span>₹ {fmt(paidAmount)}</span>
          </div>
        </div>

        {/* Right — Payment Modes (Mode, Reference No, Paid On, Amount) */}
        <div className="fee-receipt-box">
          <div className="fee-receipt-box-head fee-receipt-pm-head">
            <span>Mode</span>
            <span>Reference No</span>
            <span>Paid On</span>
            <span>Amount</span>
          </div>
          <div className="fee-receipt-box-body">
            {modes.map((pm, i) => (
              <div className="fee-receipt-pm-row" key={i}>
                <span className="fee-receipt-bold">{pm.mode || 'Cash'}</span>
                <span>{pm.referenceNo || '—'}</span>
                <span>{fmtDate(pm.date || payment.paidAt)}</span>
                <span>₹ {fmt(pm.amount)}</span>
              </div>
            ))}
          </div>
          <div className="fee-receipt-box-total">
            <span>Amount Paid</span>
            <span className="fee-receipt-green">₹ {fmt(paidAmount)}</span>
          </div>
        </div>

      </div>

      {/* ── Balance + Total ── */}
      <div className="fee-receipt-summary-row">
        <div>
          <span className="fee-receipt-sum-label">Balance Due</span>
          <span className={`fee-receipt-sum-val ${balance <= 0 ? 'fee-receipt-green' : 'fee-receipt-red'}`}>
            ₹ {fmt(balance)}
          </span>
        </div>
        <div className="fee-receipt-sum-right">
          <span className="fee-receipt-sum-label">Total Paid This Receipt</span>
          <span className="fee-receipt-sum-val fee-receipt-green">₹ {fmt(paidAmount)}</span>
        </div>
      </div>

      {/* ── Footer: Terms left | Signature right ── */}
      <div className="fee-receipt-footer">
        <div className="fee-receipt-terms">
          {college.termCondition && (
            <>
              <span className="fee-receipt-terms-title">Terms &amp; Conditions: </span>
              <span>{college.termCondition}</span>
            </>
          )}
        </div>
        <div className="fee-receipt-signature">
          <div className="fee-receipt-sig-line" />
          <span>Authorised Signature</span>
        </div>
      </div>

    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const FeeReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { allocation, payment } = location.state || {};

  if (!allocation || !payment) {
    return (
      <div className="fee-receipt-wrapper">
        <div className="fee-receipt-actions">
          <button className="fee-receipt-btn fee-receipt-btn-back" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
        </div>
        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '40px' }}>No receipt data found.</p>
      </div>
    );
  }

  const college     = allocation.college || {};
  const student     = allocation.student || {};
  const collegeLogo = college.logo ? `${API_BASE}/${college.logo.replace(/\\/g, '/')}` : null;
  const slipProps   = { allocation, payment, college, student, collegeLogo };

  return (
    <div className="fee-receipt-wrapper">
      <div className="fee-receipt-actions">
        <button className="fee-receipt-btn fee-receipt-btn-back" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <button className="fee-receipt-btn fee-receipt-btn-print" onClick={() => window.print()}>
          <FaPrint /> Print Receipt
        </button>
      </div>

      <div className="fee-receipt-a4">
        <Slip {...slipProps} copyLabel="Student Copy" />
        <div className="fee-receipt-divider" />
        <Slip {...slipProps} copyLabel="College Copy" />
      </div>
    </div>
  );
};

export default FeeReceipt;